import { useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import { QUESTIONNAIRE_STATUS, Roles } from '../../../../../../constants';
import { areUserRolesIn } from '../../../../../../helpers';
import { onboardingQuestionnaireActions } from '../../../../../../store';
import {
  COMPLETED_QUESTIONNAIRE_DOCUMENT_MUTATION_MESSAGE,
  COMPLETED_QUESTIONNAIRE_DOCUMENT_TOAST_ID,
} from '../constants';

import type { AppDispatch, RootState } from '../../../../../../store';
import type { DeleteOnboardingQuestionnaireDocumentParams } from './types';

export function useDeleteOnboardingQuestionnaireDocument() {
  const dispatch = useDispatch<AppDispatch>();
  const { getAccessTokenSilently } = useAuth0();
  const { user } = useSelector((state: RootState) => state.auth);
  const [deleteDocumentLoading, setDeleteDocumentLoading] = useState(false);

  const canDeleteDocument = areUserRolesIn(user?.roles, [Roles.Admin]);

  const deleteDocument = async ({
    onboardingQuestionnaireId,
    documentId,
    questionnaireStatus,
  }: DeleteOnboardingQuestionnaireDocumentParams): Promise<boolean> => {
    if (!canDeleteDocument) {
      return false;
    }

    if (questionnaireStatus === QUESTIONNAIRE_STATUS.COMPLETED) {
      toast.error(COMPLETED_QUESTIONNAIRE_DOCUMENT_MUTATION_MESSAGE, {
        toastId: COMPLETED_QUESTIONNAIRE_DOCUMENT_TOAST_ID,
      });
      return false;
    }

    setDeleteDocumentLoading(true);
    try {
      const result = await dispatch(
        onboardingQuestionnaireActions.deleteOnboardingQuestionnaireDocument({
          onboardingQuestionnaireId,
          documentId: String(documentId),
          getAccessTokenSilently,
        }),
      );

      if (
        onboardingQuestionnaireActions.deleteOnboardingQuestionnaireDocument.fulfilled.match(result)
      ) {
        toast.success('Document deleted successfully');
        return true;
      }

      return false;
    } finally {
      setDeleteDocumentLoading(false);
    }
  };

  return {
    canDeleteDocument,
    deleteDocument,
    deleteDocumentLoading,
  };
}
